import { useMemo } from 'react';
import Badge from '@adsk/alloy-react-badge';
import theme from '@adsk/alloy-react-theme';

import { useLocale } from '@adsk/alloy-react-locale';
import { toLocalLongDate, translate } from '../../../../helpers';
import {
  AssigneeCell,
  DueDateCell,
  LocationCell,
  RfiTypeCell,
  StatusCell,
} from '../../../../components';
import { AssigneeType, CustomAttribute } from '../../../../sdk';

import { TABLE_COLUMN_ACCESSOR_KEY, TABLE_COLUMN_ID_KEY } from '../helpers/constants';
import { CellParams } from '../helpers/types';
import { getCellDataForCustomAttributes, getCustomAttributesOptionsMap } from '../helpers/utils';
import {
  renderOverFlowTooltipCellData,
  renderTranslateOverFlowTooltip,
} from '../helpers/tableHelper';

export default function useTableColumns(customAttributes: CustomAttribute[]) {
  const { locale } = useLocale(() => undefined);
  return useMemo(() => {
    const activeCustomAttributes = customAttributes.filter(({ isActive }) => isActive);
    const customAttributesOptionsMap = getCustomAttributesOptionsMap(activeCustomAttributes);
    return [
      {
        header: translate('rpPlugin.new.status'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.STATUS,
        minSize: 180,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue }: CellParams) => <StatusCell status={getValue()} />,
      },
      {
        header: translate('rpPlugin.new.id'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.ID,
        minSize: 90,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderOverFlowTooltipCellData,
      },
      {
        header: translate('rpPlugin.new.title'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.TITLE,
        minSize: 170,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderOverFlowTooltipCellData,
      },
      {
        header: translate('rpPlugin.new.assignedTo'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.ASSIGNED_TO,
        minSize: 135,
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue, row }: CellParams) => (
          <AssigneeCell
            assignees={getValue()}
            variant={AssigneeCell.VARIANTS.TABLE_CELL}
            isTargetRfi={row.original?.bridgedTarget}
          />
        ),
      },
      {
        header: translate('rpPlugin.new.dueDate'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.DUE_DATE,
        minSize: 125,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ row }: CellParams) => <DueDateCell rowData={row?.original} />,
      },
      {
        header: translate('rpPlugin.new.rfiType'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.RFI_TYPE,
        minSize: 180,
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue }: CellParams) => <RfiTypeCell rfiTypeId={getValue()} />,
      },
      {
        header: translate('rpPlugin.new.location'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.LOCATION,
        minSize: 110,
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue }: CellParams) => <LocationCell cellData={getValue()} />,
      },
      {
        header: translate('rpPlugin.new.locationDetails'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.LOCATION_DETAILS,
        id: TABLE_COLUMN_ID_KEY.LOCATION,
        minSize: 110,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderOverFlowTooltipCellData,
      },
      {
        header: translate('rpPlugin.new.costImpact'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.COST_IMPACT,
        minSize: 110,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderTranslateOverFlowTooltip,
      },
      {
        header: translate('rpPlugin.new.scheduleImpact'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.SCHEDULE_IMPACT,
        minSize: 110,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderTranslateOverFlowTooltip,
      },
      {
        header: translate('rpPlugin.new.priority'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.PRIORITY,
        minSize: 110,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderTranslateOverFlowTooltip,
      },
      {
        header: translate('rpPlugin.new.discipline'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.DISCIPLINE,
        minSize: 110,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderTranslateOverFlowTooltip,
      },
      {
        header: translate('rpPlugin.new.category'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.CATEGORY,
        minSize: 130,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderTranslateOverFlowTooltip,
      },
      {
        header: translate('rpPlugin.new.reference'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.REFERENCE,
        minSize: 120,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: renderOverFlowTooltipCellData,
      },
      {
        header: translate('rpPlugin.new.createdBy'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.CREATED_BY,
        minSize: 200,
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue }: CellParams) => (
          <AssigneeCell
            assignees={[{ id: getValue(), type: AssigneeType.User }]}
            variant={AssigneeCell.VARIANTS.TABLE_CELL}
          />
        ),
      },
      {
        header: translate('rpPlugin.new.comments'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.COMMENTS,
        minSize: 90,
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue }: CellParams) => {
          const cellData = getValue();
          if (!cellData) return '-';
          return (
            <Badge color={theme.colors.charcoal100} textColor={theme.colors.charcoal900}>
              {cellData}
            </Badge>
          );
        },
      },
      {
        header: translate('rpPlugin.new.createdAt'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.CREATED_AT,
        minSize: 125,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ getValue }: CellParams) => (
          <div>{getValue() ? toLocalLongDate(getValue()) : '-'}</div>
        ),
      },
      ...activeCustomAttributes.map((customAttribute) => ({
        header: customAttribute.displayName,
        accessorKey: customAttribute.id,
        minSize: 150,
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        cell: getCellDataForCustomAttributes(customAttribute, customAttributesOptionsMap),
      })),
    ];
  }, [locale, customAttributes]);
}
